import { useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  rrcProfileCapabilities,
  useGetRegionComplianceProvidersQuery,
} from '@ots-frontend/ots-data';
import { useApp, useAppDensity } from '../app/AppContext';
import { AdminPage, type AdminPageProps } from '../components/AdminPage';
import { AdminPanel } from '../components/AdminPanel';
import { OtsActionButton } from '../components/OtsButton';
import { OtsInlineAlert } from '../components/OtsInlineAlert';
import { OtsMasterDetail } from '../components/OtsMasterDetail';
import { OtsEntityIdentitySelect } from '../select/OtsEntityIdentitySelect';
import { OtsThemeRoot } from '../theme/OtsTheme';
import { RrcCompanyProfilesPage } from './company-profiles/RrcCompanyProfilesPage';
import { RrcLocationProfilesPage } from './location-profiles/RrcLocationProfilesPage';
import { RrcEp9WasteProfilesPage } from './ep9-waste-profiles/RrcEp9WasteProfilesPage';

export type RrcProfileSubject = {
  type: 'COMPANY' | 'SITE' | 'WELL';
  id: string;
  name?: string;
};
export type RrcProfilesManagerProps = {
  /** Locks management to the record opened by the host. Omit to choose a record. */
  subject?: RrcProfileSubject;
  compact?: boolean;
  /** Fill a bounded host such as a WaterGo content panel. */
  layout?: AdminPageProps['layout'];
};
const recordTypes = [
  { value: 'COMPANY', label: 'Company' },
  { value: 'SITE', label: 'Site' },
  { value: 'WELL', label: 'Well' },
];
const Manager = ({
  subject,
  compact: compactProp,
  layout = 'document',
}: RrcProfilesManagerProps) => {
  const app = useApp();
  const density = useAppDensity();
  const compact = compactProp ?? density === 'compact';
  const fill = layout === 'fill';
  const discovery = useGetRegionComplianceProvidersQuery(
    app?.regionId ? { regionId: app.regionId } : skipToken,
    { refetchOnMountOrArgChange: 30, refetchOnFocus: true }
  );
  const capabilities = rrcProfileCapabilities(discovery.currentData);
  const companyAccess = capabilities.has('rrccompanyprofile_view');
  const locationAccess = capabilities.has('rrclocationprofile_view');
  const wasteAccess = capabilities.has('rrcep9wasteprofile_view');
  const allowedTypes = recordTypes.filter(
    ({ value }) =>
      wasteAccess || (value === 'COMPANY' ? companyAccess : locationAccess)
  );
  const [selection, setOwner] = useState<{
    type: string;
    id?: string;
    name?: string;
  }>(subject ?? { type: allowedTypes[0]?.value ?? 'COMPANY' });
  const owner =
    !subject && !allowedTypes.some(({ value }) => value === selection.type)
      ? {
          type: allowedTypes[0]?.value ?? selection.type,
          id: undefined,
          name: undefined,
        }
      : selection;
  const [selectedProgram, selectProgram] = useState<string>('registration');
  const isCompany = owner.type === 'COMPANY';
  const registrationAccess = isCompany ? companyAccess : locationAccess;
  const programs = [
    ...(registrationAccess
      ? [
          {
            id: 'registration',
            label: isCompany
              ? 'Company registrations'
              : 'Location registrations',
            content: (
              <>
                <p className="text-sm font-semibold text-[var(--ots-secondary)]">
                  {isCompany
                    ? 'Company registrations'
                    : 'Location registrations'}
                </p>
                <p className="mt-1 text-xs text-[var(--ots-text-muted)]">
                  RRC identifiers, permits, contacts, and signing settings.
                </p>
              </>
            ),
          },
        ]
      : []),
    ...(wasteAccess
      ? [
          {
            id: 'waste',
            label: 'EP-9 waste profiles',
            content: (
              <>
                <p className="text-sm font-semibold text-[var(--ots-secondary)]">
                  EP-9 waste profiles
                </p>
                <p className="mt-1 text-xs text-[var(--ots-text-muted)]">
                  Waste characterization, generator information, and version
                  history.
                </p>
              </>
            ),
          },
        ]
      : []),
  ];
  const selected =
    programs.find(({ id }) => id === selectedProgram) ?? programs[0];
  const validOwner = allowedTypes.some(({ value }) => value === owner.type);
  const recordLabel =
    recordTypes.find(({ value }) => value === owner.type)?.label ?? 'Record';
  return (
    <OtsThemeRoot className={fill ? 'h-full min-h-0' : undefined}>
      <AdminPage
        title="RRC profiles"
        eyebrow={
          subject
            ? `${recordLabel} · ${subject.name ?? subject.id}`
            : 'Texas RRC'
        }
        fullWidth
        layout={layout}
        density={compact ? 'compact' : 'default'}
        subtitle="Manage registrations, signing settings, and EP-9 waste profiles for companies, sites, and wells."
      >
        <div
          className={
            fill
              ? 'mt-4 flex min-h-0 flex-1 flex-col gap-4 overflow-auto'
              : 'mt-5 space-y-4'
          }
        >
          {!app?.regionId ? (
            <OtsInlineAlert title="RRC profiles unavailable">
              Select a region regulated by the Texas RRC to manage profiles.
            </OtsInlineAlert>
          ) : discovery.isError ? (
            <OtsInlineAlert
              title="Unable to check RRC availability"
              tone="warning"
              action={
                <OtsActionButton
                  size="compact"
                  onClick={() => void discovery.refetch()}
                >
                  Retry
                </OtsActionButton>
              }
            >
              Please retry to load region compliance settings.
            </OtsInlineAlert>
          ) : !discovery.currentData ? (
            <OtsInlineAlert title="Checking compliance availability">
              Loading region compliance settings…
            </OtsInlineAlert>
          ) : !allowedTypes.length ? (
            <OtsInlineAlert title="RRC profiles unavailable" tone="warning">
              RRC profiles are not available for this region or your
              permissions.
            </OtsInlineAlert>
          ) : !validOwner ? (
            <OtsInlineAlert title="Record unavailable" tone="warning">
              RRC profile management is not available for this record.
            </OtsInlineAlert>
          ) : (
            <>
              {!subject && (
                <AdminPanel
                  className="shrink-0"
                  title="Manage profiles for"
                  compact={compact}
                  bodyClassName="p-4"
                >
                  <div className="max-w-3xl">
                    <OtsEntityIdentitySelect
                      label="Record"
                      entityTypes={allowedTypes}
                      entityType={owner.type}
                      value={owner.id}
                      entityName={owner.name}
                      regionId={app?.regionId}
                      size={compact ? 'compact' : 'default'}
                      onChange={(id, type) => {
                        setOwner({ id, type });
                        selectProgram('registration');
                      }}
                      onNameChange={(name) =>
                        setOwner((value) => ({ ...value, name }))
                      }
                    />
                  </div>
                </AdminPanel>
              )}
              {!owner.id ? (
                <OtsInlineAlert title="Select a record">
                  Choose a company, site, or well to view its profiles or create
                  its first registration.
                </OtsInlineAlert>
              ) : (
                selected && (
                  <OtsMasterDetail
                    fill={fill}
                    navigationTitle="Profile types"
                    navigationLabel="RRC profile types"
                    items={programs}
                    selectedId={selected.id}
                    onSelect={selectProgram}
                    compact={compact}
                    hideNavigation={programs.length === 1}
                  >
                    <section
                      key={`${owner.type}:${owner.id}:${selected.id}`}
                      aria-label={selected.label}
                      className={
                        fill ? 'flex min-h-0 flex-1 flex-col' : undefined
                      }
                    >
                      {selected.id === 'waste' ? (
                        <RrcEp9WasteProfilesPage
                          objectId={owner.id}
                          objectType={owner.type}
                          objectName={owner.name}
                          compact={compact}
                          embedded
                          layout={layout}
                        />
                      ) : isCompany ? (
                        <RrcCompanyProfilesPage
                          companyId={owner.id}
                          companyName={owner.name}
                          compact={compact}
                          embedded
                          layout={layout}
                        />
                      ) : (
                        <RrcLocationProfilesPage
                          locationId={owner.id}
                          locationType={owner.type}
                          locationName={owner.name}
                          compact={compact}
                          embedded
                          layout={layout}
                        />
                      )}
                    </section>
                  </OtsMasterDetail>
                )
              )}
            </>
          )}
        </div>
      </AdminPage>
    </OtsThemeRoot>
  );
};
export const RrcProfilesManager = (props: RrcProfilesManagerProps) => {
  const app = useApp();
  return (
    <Manager
      key={JSON.stringify([
        app?.regionId,
        props.subject?.type,
        props.subject?.id,
      ])}
      {...props}
    />
  );
};
